import React from 'react';
import { Box } from '@mui/material';
import { brandColors } from '../theme/colors';

export interface LogoProps {
  variant?: 'full' | 'icon';
  height?: number;
  color?: 'default' | 'white' | 'dark';
  onClick?: () => void;
}

export const Logo: React.FC<LogoProps> = ({
  variant = 'full',
  height = 40,
  color = 'default',
  onClick,
}) => {
  const getTextColor = () => {
    switch (color) {
      case 'white':
        return brandColors.white;
      case 'dark':
        return brandColors.indigo;
      default:
        return brandColors.indigo;
    }
  };

  const getGoldColor = () => {
    return color === 'white' ? brandColors.white : brandColors.gold;
  };

  // Use the actual Horwath HTL logo design based on logo-full.svg
  const iconSvg = (
    <svg
      width={height}
      height={height}
      viewBox="0 0 135.7 135.7"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path 
        d="M82,0.7c-0.5-0.9-1.3-1-1.9-0.1L0.3,135c-0.5,0.9-0.4,1,0.2,0.2l78.9-95.3c0.7-0.8,0.8-0.7,0.3,0.2L25.1,135c-0.5,0.9-0.4,1,0.3,0.2L90,57.1c0.7-0.8,0.8-0.7,0.3,0.2l-42.5,77.7c-0.5,0.9-0.4,1,0.3,0.2l51.5-62.3c0.7-0.8,1.8-0.8,2.4,0l50,62.2c0.7,0.8,0.8,0.7,0.3-0.2L82,0.7z" 
        fill={getGoldColor()} 
      />
    </svg>
  );

  const fullSvg = (
    <svg
      height={height}
      viewBox="0 0 844 135.7"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ width: 'auto' }}
    >
      {/* Icon part */}
      <path 
        d="M82,0.7c-0.5-0.9-1.3-1-1.9-0.1L0.3,135c-0.5,0.9-0.4,1,0.2,0.2l78.9-95.3c0.7-0.8,0.8-0.7,0.3,0.2L25.1,135c-0.5,0.9-0.4,1,0.3,0.2L90,57.1c0.7-0.8,0.8-0.7,0.3,0.2l-42.5,77.7c-0.5,0.9-0.4,1,0.3,0.2l51.5-62.3c0.7-0.8,1.8-0.8,2.4,0l50,62.2c0.7,0.8,0.8,0.7,0.3-0.2L82,0.7z"
        fill={getGoldColor()}
      />
      
      {/* Text: Horwath HTL */}
      <g fill={getTextColor()}>
        <path d="M263.6,106.7h-11.5V65.6h-40.4v41.1h-11.5V24h11.5v33.3h40.4V24h11.5V106.7z"/>
        <path d="M269.9,76.4c0-19.1,14.2-30.2,29.1-30.2c14.9,0,29.2,11.1,29.2,30.2c0,18.3-11.7,31.7-29.2,31.7C281.6,108.1,269.9,94.8,269.9,76.4z M281.4,76.4c0,11.1,3.8,24.1,17.6,24.1c13.9,0,17.7-13,17.7-24.1c0-10.6-4.4-22.7-17.7-22.7C285.7,53.8,281.4,65.8,281.4,76.4z"/>
        <path d="M343.6,55l4.3-4.5c2.5-2.5,6.4-4.3,8.9-4.3c2.6,0,6.5,1.9,9.8,5.8l-4.8,6.8c-2.6-1.3-5.2-2.5-7.9-2.5c-5.4,0-9.9,5.4-9.9,18.5v31.8h-10.6v-59h10.4V55z"/>
        <path d="M418.3,47.6c5.8,14.1,11.6,28.2,17.2,45.1h0.2c4.6-17.4,9.4-31.8,14.2-46.3l9.8,1.7L439,106.7h-7.4c-5.9-14.6-11.9-29.3-17.7-46.1h-0.2c-5.7,16.8-11.5,31.4-17.2,46.1h-7l-20.4-57.6l10.4-2.6c4.8,14.5,9.8,28.9,14.5,46.3h0.2c5.7-16.9,11.5-31.1,17.2-45.1H418.3z"/>
        <path d="M489.9,100.3c-4.1,4.1-9.3,7.8-14.8,7.8c-9.9,0-16.8-7.1-16.8-18.2c0-10.3,8.7-16.8,21-20.4l14.3-4.5c-0.5-7.2-6-11.8-11.8-11.8c-6.8,0-11.2,2.8-15.5,6.4l-4.1-5.3c5.9-5.7,11.9-8,20.2-8c8.9,0,21.8,2.8,21.8,20.1v26c0,4.5,0.6,10.2,2.6,14.4h-11.6c-1.4-2.5-1.7-6.3-1.7-10L489.9,100.3z M483.5,75c-9.3,2.8-14.2,7.3-14.2,14.8c0,6.1,4.3,10,8.5,10c7.6,0,17.2-9.3,15.8-27.8L483.5,75z"/>
        <path d="M525.5,47.6H541v6.1h-15.5v33.4c0,8.3,1.7,13.2,8.1,13.2c3.9,0,6.1-0.9,8.1-2l2.6,5.9c-3.7,2.4-8,3.8-12,3.8c-11.8,0-17.5-6.9-17.5-18.9V53.8h-8.3v-6.1h8.3V33.5l10.6-3.5V47.6z"/>
        <path d="M559.3,55c5.5-5.9,14.1-8.7,20.2-8.7c13.6,0,20.9,7.6,20.9,22.4v38h-10.6V70.9c0-11.1-4.7-16.9-14.3-16.9c-8.4,0-16.1,5.8-16.1,17.1v35.5h-10.6V19.9h10.5V55z"/>
        <path d="M712.7,106.7h-11.5V65.6h-40.4v41.1h-11.5V24h11.5v33.3h40.4V24h11.5V106.7z"/>
        <path d="M749.6,106.7V32.3h-26.5V24h64.5v8.3h-26.6v74.4H749.6z"/>
        <path d="M798.2,24h11.5v74.4H844v8.3h-45.8V24z"/>
      </g>
    </svg>
  );

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        cursor: onClick ? 'pointer' : 'default',
      }}
      onClick={onClick}
    >
      {variant === 'icon' ? iconSvg : fullSvg}
    </Box>
  );
};